/**
 * Custom hook for managing notes on the Notes viewing page with lazy loading by chapter
 */

import { useState, useCallback, useEffect } from 'react';
import { api } from '../config/api';
import { ReadingNote, NoteType, ReadingNoteCreate } from '../types/reading-notes';

interface ChapterSummary {
  chapter_number: number;
  chapter_name: string;
  note_count: number;
  page_range: [number, number] | null;
}

interface ChaptersSummaryResponse {
  book_id: number;
  book_title: string;
  total_notes: number;
  chapters: ChapterSummary[];
}

interface NotesResponse {
  notes: ReadingNote[];
  total: number;
  page: number;
  per_page: number;
}

interface StatsResponse {
  total_notes: number;
  highlight_count: number;
  thought_count: number;
  ai_answer_count: number;
  indexed_count: number;
  total_pages_with_notes: number;
  total_chapters: number;
}

interface ChapterPagination {
  page: number;
  hasMore: boolean;
  total: number;
}

const NOTES_PER_PAGE = 50;

export function useBookNotes(bookId: string) {
  const [chaptersSummary, setChaptersSummary] = useState<ChapterSummary[]>([]);
  const [bookTitle, setBookTitle] = useState<string>('');
  const [totalNotes, setTotalNotes] = useState(0);
  const [loadedChapters, setLoadedChapters] = useState<Map<number, ReadingNote[]>>(new Map());
  const [chapterPagination, setChapterPagination] = useState<Map<number, ChapterPagination>>(new Map());
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<Set<NoteType>>(new Set<NoteType>(['highlight', 'thought', 'chat_answer']));

  // Fetch stats (on mount)
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get<StatsResponse>(`/api/books/${bookId}/notes/stats`);
      setStats(response.data);
    } catch (err: any) {
      console.error('Failed to fetch stats:', err);
      // Don't set error state for stats - it's not critical
    }
  }, [bookId]);

  // Fetch chapters summary (on mount)
  const fetchChaptersSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get<ChaptersSummaryResponse>(`/api/books/${bookId}/notes/chapters/summary`);

      setChaptersSummary(response.data.chapters);
      setBookTitle(response.data.book_title);
      setTotalNotes(response.data.total_notes);
    } catch (err: any) {
      // Handle FastAPI validation errors (422) which return an array of error objects
      const errorMsg = Array.isArray(err.response?.data?.detail)
        ? err.response.data.detail.map((e: any) => e.msg).join(', ')
        : err.response?.data?.detail || 'Failed to fetch chapters summary';
      setError(errorMsg);
      console.error('Failed to fetch chapters summary:', err);
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  // Fetch notes for a specific chapter (lazy loading)
  // Use forceRefetch=true to refetch even if already loaded (e.g., for polling)
  // Use silent=true to suppress loading state (for background polling)
  const fetchChapterNotes = useCallback(async (chapterNumber: number, forceRefetch: boolean = false, silent: boolean = false) => {
    // Check if already loaded (unless forcing refetch)
    if (!forceRefetch && loadedChapters.has(chapterNumber)) {
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      const response = await api.get<NotesResponse>(`/api/books/${bookId}/notes`, {
        params: {
          chapter_number: chapterNumber,
          per_page: NOTES_PER_PAGE,
          page_number: 1
        }
      });

      const newNotes = response.data.notes.map(note => ({
        ...note,
        created_at: new Date(note.created_at),
        updated_at: new Date(note.updated_at),
        indexed_at: note.indexed_at ? new Date(note.indexed_at) : undefined
      }));

      setLoadedChapters(prev => {
        const newMap = new Map(prev);
        newMap.set(chapterNumber, newNotes);
        return newMap;
      });

      // Track pagination state
      setChapterPagination(prev => {
        const newMap = new Map(prev);
        newMap.set(chapterNumber, {
          page: 1,
          hasMore: response.data.notes.length === NOTES_PER_PAGE && newNotes.length < response.data.total,
          total: response.data.total
        });
        return newMap;
      });
    } catch (err: any) {
      // Handle FastAPI validation errors (422) which return an array of error objects
      const errorMsg = Array.isArray(err.response?.data?.detail)
        ? err.response.data.detail.map((e: any) => e.msg).join(', ')
        : err.response?.data?.detail || 'Failed to fetch chapter notes';
      setError(errorMsg);
      console.error('Failed to fetch chapter notes:', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [bookId, loadedChapters]);

  // Load more notes for a chapter (infinite scroll)
  const loadMoreNotes = useCallback(async (chapterNumber: number) => {
    const pagination = chapterPagination.get(chapterNumber);
    if (!pagination || !pagination.hasMore || loadingMore) {
      return;
    }

    try {
      setLoadingMore(true);
      const nextPage = pagination.page + 1;

      const response = await api.get<NotesResponse>(`/api/books/${bookId}/notes`, {
        params: {
          chapter_number: chapterNumber,
          per_page: NOTES_PER_PAGE,
          page_number: nextPage
        }
      });

      const newNotes = response.data.notes.map(note => ({
        ...note,
        created_at: new Date(note.created_at),
        updated_at: new Date(note.updated_at),
        indexed_at: note.indexed_at ? new Date(note.indexed_at) : undefined
      }));

      // Append new notes to existing ones
      setLoadedChapters(prev => {
        const newMap = new Map(prev);
        const existingNotes = newMap.get(chapterNumber) || [];
        newMap.set(chapterNumber, [...existingNotes, ...newNotes]);
        return newMap;
      });

      // Update pagination state
      setChapterPagination(prev => {
        const newMap = new Map(prev);
        const existingNotes = loadedChapters.get(chapterNumber) || [];
        newMap.set(chapterNumber, {
          page: nextPage,
          hasMore: response.data.notes.length === NOTES_PER_PAGE && (existingNotes.length + newNotes.length) < response.data.total,
          total: response.data.total
        });
        return newMap;
      });
    } catch (err: any) {
      console.error('Failed to load more notes:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [bookId, chapterPagination, loadingMore, loadedChapters]);

  // Check if a chapter has more notes to load
  const hasMoreNotes = useCallback((chapterNumber: number): boolean => {
    const pagination = chapterPagination.get(chapterNumber);
    return pagination?.hasMore ?? false;
  }, [chapterPagination]);

  // Get notes for a specific chapter (from loaded data)
  const getChapterNotes = useCallback((chapterNumber: number): ReadingNote[] | null => {
    return loadedChapters.get(chapterNumber) || null;
  }, [loadedChapters]);

  // Update a note
  const updateNote = useCallback(async (noteId: string, updates: Partial<ReadingNote>) => {
    try {
      const response = await api.patch(`/api/books/${bookId}/notes/${noteId}`, updates);
      const updatedNote = {
        ...response.data,
        created_at: new Date(response.data.created_at),
        updated_at: new Date(response.data.updated_at),
        indexed_at: response.data.indexed_at ? new Date(response.data.indexed_at) : undefined
      };

      // Update the note in loaded chapters
      setLoadedChapters(prev => {
        const newMap = new Map(prev);
        newMap.forEach((notes, chapterNum) => {
          const index = notes.findIndex(n => n.id === noteId);
          if (index !== -1) {
            const newNotes = [...notes];
            newNotes[index] = updatedNote;
            newMap.set(chapterNum, newNotes);
          }
        });
        return newMap;
      });

      return updatedNote;
    } catch (err: any) {
      throw new Error(err.response?.data?.detail || 'Failed to update note');
    }
  }, [bookId]);

  // Delete a note
  const deleteNote = useCallback(async (noteId: string) => {
    try {
      await api.delete(`/api/books/${bookId}/notes/${noteId}`);

      // Remove the note from loaded chapters
      setLoadedChapters(prev => {
        const newMap = new Map(prev);
        newMap.forEach((notes, chapterNum) => {
          const newNotes = notes.filter(n => n.id !== noteId);
          newMap.set(chapterNum, newNotes);
        });
        return newMap;
      });

      // Update total count
      setTotalNotes(prev => Math.max(0, prev - 1));

      // Update chapter summary counts (keep all chapters, even with 0 notes)
      setChaptersSummary(prev =>
        prev.map(chapter => {
          const chapterNotes = loadedChapters.get(chapter.chapter_number);
          if (chapterNotes && chapterNotes.some(n => n.id === noteId)) {
            return { ...chapter, note_count: Math.max(0, chapter.note_count - 1) };
          }
          return chapter;
        })
      );
    } catch (err: any) {
      throw new Error(err.response?.data?.detail || 'Failed to delete note');
    }
  }, [bookId, loadedChapters]);

  // Create a new note
  const createNote = useCallback(async (noteData: Omit<ReadingNoteCreate, 'book_id'>) => {
    try {
      const response = await api.post(`/api/books/${bookId}/notes`, {
        ...noteData,
        book_id: bookId
      });

      const newNote: ReadingNote = {
        ...response.data,
        created_at: new Date(response.data.created_at),
        updated_at: new Date(response.data.updated_at),
        indexed_at: response.data.indexed_at ? new Date(response.data.indexed_at) : undefined
      };

      // Update total count
      setTotalNotes(prev => prev + 1);

      // Optimistically add the note to the loaded chapters cache
      const chapterNumber = newNote.chapter_number ?? (noteData.page_number === 0 ? 0 : null);
      if (chapterNumber !== null) {
        setLoadedChapters(prev => {
          const newMap = new Map(prev);
          const existingNotes = newMap.get(chapterNumber) || [];
          newMap.set(chapterNumber, [newNote, ...existingNotes]);
          return newMap;
        });
      }

      // Refetch chapters summary to get updated data
      fetchChaptersSummary();

      return newNote;
    } catch (err: any) {
      throw new Error(err.response?.data?.detail || 'Failed to create note');
    }
  }, [bookId, fetchChaptersSummary]);

  // Re-index a note (for retrying failed indexing)
  const reindexNote = useCallback(async (noteId: string) => {
    try {
      await api.post(`/api/books/${bookId}/notes/${noteId}/reindex`);

      // Update the note's indexing status in loaded chapters
      setLoadedChapters(prev => {
        const newMap = new Map(prev);
        newMap.forEach((notes, chapterNum) => {
          const index = notes.findIndex(n => n.id === noteId);
          if (index !== -1) {
            const newNotes = [...notes];
            newNotes[index] = {
              ...newNotes[index],
              is_indexed: false,
              indexing_status: 'pending',
              indexing_error: undefined
            };
            newMap.set(chapterNum, newNotes);
          }
        });
        return newMap;
      });
    } catch (err: any) {
      throw new Error(err.response?.data?.detail || 'Failed to trigger re-indexing');
    }
  }, [bookId]);

  // Toggle note type filter
  const toggleTypeFilter = useCallback((type: NoteType) => {
    setSelectedTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  }, []);

  // Statistics - use fetched stats if available, otherwise defaults
  const statsObject = {
    totalNotes: stats?.total_notes || totalNotes,
    totalChapters: stats?.total_chapters || chaptersSummary.length,
    totalPages: stats?.total_pages_with_notes || 0,
    highlightCount: stats?.highlight_count || 0,
    thoughtCount: stats?.thought_count || 0,
    aiAnswerCount: stats?.ai_answer_count || 0,
    indexedCount: stats?.indexed_count || 0
  };

  // Initial load
  useEffect(() => {
    fetchChaptersSummary();
    fetchStats();
  }, [fetchChaptersSummary, fetchStats]);

  return {
    chaptersSummary,
    bookTitle,
    totalNotes,
    loadedChapters,
    getChapterNotes,
    loading,
    loadingMore,
    error,
    searchQuery,
    setSearchQuery,
    selectedTypes,
    toggleTypeFilter,
    stats: statsObject,
    fetchChapterNotes,
    loadMoreNotes,
    hasMoreNotes,
    createNote,
    updateNote,
    deleteNote,
    reindexNote,
    refetch: () => {
      fetchChaptersSummary();
      fetchStats();
    }
  };
}
